import type { Observable } from 'rxjs';
import type { SignInOutcome, TwoFactorEnrollment, TwoFactorProof } from './authentication.types';

/**
 * Behavior-only authentication contract. Token state (accessToken,
 * refreshToken, authenticated, credentials I/O) lives in `TokenStore`.
 * Concrete adapters inject both `TokenStore` and any required transport
 * (e.g. Apollo) and delegate state mutations to the store.
 *
 * `signInUsingRefreshToken()` is deliberately NOT a member of this class: the refresh grant is
 * owned exclusively by `SessionRecoveryCoordinatorService`'s single flight, so no caller can issue
 * a second concurrent grant against the single-use refresh token (design.md Decision 1).
 */
export abstract class AuthenticationService {
  /** Creates the hub session, or resolves `second-factor` with the step and challenge to continue with. */
  abstract signIn(credentials: { email: string; password: string }): Observable<SignInOutcome>;
  /** Second step of a sign-in: completes the hub session with a TOTP or recovery code. */
  abstract verifySecondFactor(challenge: string, proof: TwoFactorProof): Observable<boolean>;
  /** Forced enrollment, step 1: the secret and `otpauth://` URI for the challenge's user. */
  abstract startTwoFactorEnrollment(challenge: string): Observable<TwoFactorEnrollment>;
  /** Forced enrollment, step 2: confirms the first code, completes the session, returns the recovery codes. */
  abstract confirmTwoFactorEnrollment(challenge: string, code: string): Observable<string[]>;
  abstract signOut(): Observable<boolean>;
  abstract signUp(user: { name: string; email: string; password: string }): Observable<boolean>;

  abstract forgotPassword(email: string): Observable<boolean>;
  abstract resetPassword(password: string, token: string): Observable<boolean>;

  /** Delegates every recovery decision to `SessionRecoveryCoordinatorService`. MUST NOT navigate —
   * it runs inside `canActivate`; a terminal outcome resolves `false` and the router redirects. */
  abstract check(): Observable<boolean>;

  /**
   * Starts impersonating `accountId`: mints a short-lived, non-renewable access token on behalf
   * of the target account, stacks it over the operator's own session (`TokenStore.beginImpersonation`),
   * and re-materializes session-scoped state. Resolves `false` (session untouched) on any failure —
   * a veto refusal, a network error, or a malformed response.
   */
  abstract impersonate(accountId: string): Observable<boolean>;
  /**
   * Ends the active impersonation: restores the operator's own stashed session
   * (`TokenStore.endImpersonation`) and re-materializes session-scoped state. The impersonation
   * access token is revoked server-side first (RFC 7009, best-effort, never awaited), so it stops
   * working before its expiry.
   * Safe to call when no impersonation is active (idempotent, mirrors `TokenStore.endImpersonation`).
   *
   * Emits `true` only when the rollback ACTUALLY happened, and `false` when there was no stashed
   * session to restore — in which case nothing changed: the active credential is still the
   * impersonation token. Callers MUST check it before announcing a return to the operator's own
   * session (finding C10); with no session to return to, ending the session is the coherent
   * outcome.
   */
  abstract stopImpersonation(): Observable<boolean>;
}
